import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  Calendar, 
  MessageSquare, 
  Award, 
  Clock,
  Sparkles,
  Flame
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';

interface StatsData {
  totalSessions: number;
  totalMinutes: number;
  currentStreak: number;
  longestStreak: number;
  creditsUsed: number;
  creditsRemaining: number;
  joinedDate: string;
  lastActiveDate: string;
  favoriteTools: string[];
}

export function ProgressStats({ userId }: { userId: string }) {
  const [stats, setStats] = useState<StatsData>({
    totalSessions: 0,
    totalMinutes: 0,
    currentStreak: 0,
    longestStreak: 0,
    creditsUsed: 0,
    creditsRemaining: 0,
    joinedDate: new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
    favoriteTools: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [userId]);

  const fetchStats = async () => {
    try {
      // Fetch reflection conversations count
      const { count: sessionsCount } = await supabase
        .from('therapy_conversations')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Fetch daily check-ins for streak calculation
      const { data: checkIns } = await supabase
        .from('daily_checkins')
        .select('date, created_at')
        .eq('user_id', userId)
        .order('date', { ascending: false });

      // Fetch credit transactions
      const { data: transactions } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId);

      // Fetch user profile for joined date
      const { data: profile } = await supabase
        .from('profiles')
        .select('created_at')
        .eq('user_id', userId)
        .single();

      // Calculate streaks
      let currentStreak = 0;
      let longestStreak = 0;
      let tempStreak = 0;

      if (checkIns && checkIns.length > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < checkIns.length; i++) {
          const checkInDate = new Date(checkIns[i].date);
          checkInDate.setHours(0, 0, 0, 0);
          const expectedDate = new Date(today);
          expectedDate.setDate(expectedDate.getDate() - i);

          if (checkInDate.getTime() === expectedDate.getTime()) {
            tempStreak++;
            if (i === 0) currentStreak = tempStreak;
          } else {
            if (tempStreak > longestStreak) longestStreak = tempStreak;
            tempStreak = 0;
          }
        }
        if (tempStreak > longestStreak) longestStreak = tempStreak;
        if (!currentStreak && tempStreak > 0) currentStreak = tempStreak;
      }

      // Calculate credits
      const creditsUsed = transactions?.filter(t => t.transaction_type === 'debit').reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0;
      const creditsRemaining = transactions?.[0]?.balance_after || 0;

      setStats({
        totalSessions: sessionsCount || 0,
        totalMinutes: (sessionsCount || 0) * 15, // Approximate
        currentStreak,
        longestStreak,
        creditsUsed,
        creditsRemaining,
        joinedDate: profile?.created_at || new Date().toISOString(),
        lastActiveDate: checkIns?.[0]?.created_at || new Date().toISOString(),
        favoriteTools: ['Therapy Chat', 'Daily Check-in'], // Could be calculated from usage
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const daysSinceJoined = differenceInDays(new Date(), new Date(stats.joinedDate));

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSessions}</div>
            <p className="text-xs text-muted-foreground">
              ~{stats.totalMinutes} minutes total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Current Streak</CardTitle>
            <Flame className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.currentStreak}</div>
            <p className="text-xs text-muted-foreground">
              days in a row
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Longest Streak</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.longestStreak}</div>
            <p className="text-xs text-muted-foreground">
              personal best
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Member For</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{daysSinceJoined}</div>
            <p className="text-xs text-muted-foreground">
              days
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Credit Usage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Credit Usage
          </CardTitle>
          <CardDescription>Track your AI reflection credit consumption</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Used this month</span>
              <span className="font-medium">{stats.creditsUsed} credits</span>
            </div>
            <Progress value={(stats.creditsUsed / (stats.creditsUsed + stats.creditsRemaining)) * 100} />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <p className="text-2xl font-bold">{stats.creditsRemaining}</p>
              <p className="text-xs text-muted-foreground">Credits Remaining</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.creditsUsed}</p>
              <p className="text-xs text-muted-foreground">Credits Used</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
            <div>
              <p className="font-medium text-sm">Joined Therappi</p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(stats.joinedDate), 'PPP')}
              </p>
            </div>
            <Badge variant="secondary">Start</Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
            <div>
              <p className="font-medium text-sm">Last Active</p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(stats.lastActiveDate), 'PPP')}
              </p>
            </div>
            <Badge variant="outline">Recent</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Favourite Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Your Favourite Tools
          </CardTitle>
          <CardDescription>Most frequently used features</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {stats.favoriteTools.map((tool, index) => (
              <Badge key={index} variant="secondary" className="text-sm">
                {tool}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
